import {
  type TemplateLang,
  shared,
  fleet,
  tools,
} from './templateTranslations';

export interface TemplateData {
  /** Key-value pairs to display in the document (e.g. licensePlate, tagNumber, mileage) */
  fields?: Record<string, string>;
  /** Photo URLs to embed in the document */
  photoUrls?: string[];
}

export interface DocumentTemplate {
  id: string;
  title: string;
  description: string;
  documentType: string;
  icon: 'directions_car' | 'construction';
  generateHtml: (workerName: string, date: string, language?: TemplateLang, companyName?: string, templateData?: TemplateData) => string;
}

export type { TemplateLang };
export { TEMPLATE_LANGUAGES } from './templateTranslations';

const sharedStyles = `
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, 'Segoe UI', Arial, sans-serif;
    color: #222;
    margin: 0;
    padding: 20px;
    line-height: 1.7;
    font-size: 16px;
    background: #fff;
  }
  h1 {
    font-size: 22px;
    color: #006838;
    border-bottom: 3px solid #006838;
    padding-bottom: 10px;
    margin-top: 0;
  }
  h2 {
    font-size: 18px;
    color: #006838;
    margin-top: 28px;
    margin-bottom: 8px;
  }
  .summary-box {
    background: #e8f5e9;
    border-left: 4px solid #006838;
    border-radius: 6px;
    padding: 16px 18px;
    margin: 16px 0 24px 0;
    font-size: 15px;
    line-height: 1.6;
  }
  .summary-box p { margin: 4px 0; }
  .callout {
    background: #fff3e0;
    border-left: 4px solid #e65100;
    border-radius: 6px;
    padding: 12px 16px;
    margin: 12px 0;
    font-size: 15px;
  }
  .callout-danger {
    background: #fce4ec;
    border-left: 4px solid #c62828;
  }
  .section {
    margin-bottom: 20px;
    padding: 14px 16px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px solid #eee;
  }
  .section-title {
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 8px;
    color: #333;
  }
  .nl { color: #444; font-size: 15px; margin: 4px 0; }
  .tl { color: #666; font-size: 14px; font-style: italic; margin: 2px 0 8px 0; }
  ul { margin: 6px 0; padding-left: 20px; }
  li { margin-bottom: 4px; font-size: 15px; }
  li .tl { font-size: 13px; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 15px;
  }
  th {
    background: #006838;
    color: white;
    text-align: left;
    padding: 10px 12px;
    font-size: 14px;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e0e0e0;
  }
  tr:nth-child(even) { background: #f5f5f5; }
  .penalty-cost {
    text-align: right;
    font-weight: 700;
    color: #c62828;
    white-space: nowrap;
  }
  .contact-box {
    background: #e3f2fd;
    border: 2px solid #1565c0;
    border-radius: 8px;
    padding: 14px 18px;
    margin: 16px 0;
    text-align: center;
  }
  .contact-box .phone {
    font-size: 22px;
    font-weight: 800;
    color: #1565c0;
    letter-spacing: 1px;
  }
  .signature-section {
    margin-top: 32px;
    border-top: 2px solid #ccc;
    padding-top: 20px;
  }
  .sig-line {
    border-bottom: 2px solid #333;
    width: 280px;
    margin-top: 50px;
    display: inline-block;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .company { font-weight: 800; font-size: 18px; color: #006838; }
  .meta { color: #666; font-size: 14px; }
  .highlight { background: #fff9c4; padding: 2px 6px; border-radius: 3px; font-weight: 600; }
`;

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Replace {company} placeholder in a translated string */
function t(s: string, company: string): string {
  return s.replace(/\{company\}/g, esc(company));
}

/** Render a bilingual bullet: NL line + translated line underneath */
function bil(nlText: string, tlText: string, isSame: boolean): string {
  if (isSame) return `<span class="nl">${nlText}</span>`;
  return `<span class="nl">${nlText}</span><br/><span class="tl">${tlText}</span>`;
}

/** Render key-value fields as a detail table */
function renderFields(fields: Record<string, string>): string {
  const rows = Object.entries(fields)
    .filter(([, v]) => v)
    .map(([k, v]) => `<tr><td style="padding:4px 12px 4px 0;font-weight:600;white-space:nowrap">${esc(k)}</td><td style="padding:4px 0">${esc(v)}</td></tr>`)
    .join('\n');
  if (!rows) return '';
  return `<div class="section"><div class="section-title">&#x1F4CB; Details</div><table style="width:100%;border-collapse:collapse">${rows}</table></div>`;
}

/** Render photo URLs as an image gallery */
function renderPhotos(photoUrls: string[]): string {
  if (!photoUrls.length) return '';
  const imgs = photoUrls.map(
    (url) => `<img src="${esc(url)}" alt="Vehicle photo" style="max-width:200px;max-height:150px;border-radius:4px;border:1px solid #ddd" />`,
  ).join('\n');
  return `<div class="section"><div class="section-title">&#x1F4F7; Photos</div><div style="display:flex;flex-wrap:wrap;gap:8px">${imgs}</div></div>`;
}

export const documentTemplates: DocumentTemplate[] = [
  {
    id: 'fleet_driver_agreement',
    title: 'Bestuurderovereenkomst / Driver\'s Agreement',
    description: 'General terms and conditions for company vehicle usage, including care responsibilities, fuel card rules, and penalty schedule.',
    documentType: 'contract',
    icon: 'directions_car',
    generateHtml: (workerName: string, date: string, language: TemplateLang = 'en', companyName?: string, templateData?: TemplateData) => {
      const company = companyName || 'Optima Uitzendburo B.V.';
      const w = esc(workerName);
      const c = esc(company);
      const nl = fleet.nl;
      const tl = fleet[language];
      const sNl = shared.nl;
      const sTl = shared[language];
      const same = language === 'nl';
      // If language is NL, use EN as the second language
      const tl2 = same ? fleet.en : tl;
      const sTl2 = same ? shared.en : sTl;
      const isSame = false; // always show bilingual

      const b = (nlText: string, tlText: string) => bil(nlText, tlText, false);
      const fieldsHtml = templateData?.fields ? renderFields(templateData.fields) : '';
      const photosHtml = templateData?.photoUrls?.length ? renderPhotos(templateData.photoUrls) : '';

      return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>${sharedStyles}</style></head><body>
<div class="header">
  <div class="company">${c}</div>
  <div class="meta">${esc(date)}</div>
</div>

<h1>&#x1F697; ${esc(nl.title)} / ${esc(tl2.title)}</h1>
<p><strong>${w}</strong></p>

${fieldsHtml}
${photosHtml}

<div class="summary-box">
  <p><strong>&#x1F4CB; ${esc(sNl.summaryTitle)} / ${esc(sTl2.summaryTitle)}</strong></p>
  <p>${t(nl.summary, company)}</p>
  <p><em>${t(tl2.summary, company)}</em></p>
</div>

<div class="section">
  <div class="section-title">&#x1F697; 1. ${esc(nl.s1_title)} / ${esc(tl2.s1_title)}</div>
  <ul>
    <li>${b(t(nl.s1_car_property, company), t(tl2.s1_car_property, company))}</li>
    <li>${b(nl.s1_full_tank, tl2.s1_full_tank)}</li>
    <li>${b(nl.s1_return_clean, tl2.s1_return_clean)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F4B3; 2. ${esc(nl.s2_title)} / ${esc(tl2.s2_title)}</div>
  <ul>
    <li>${b(nl.s2_license, tl2.s2_license)}</li>
    <li>${b(nl.s2_rules, tl2.s2_rules)}</li>
    <li>${b(nl.s2_fines, tl2.s2_fines)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F4CF; 3. ${esc(nl.s3_title)} / ${esc(tl2.s3_title)}</div>
  <ul>
    <li>${b(nl.s3_max_km, tl2.s3_max_km)}</li>
    <li>${b(nl.s3_extra_km, tl2.s3_extra_km)}</li>
    <li>${b(nl.s3_gps, tl2.s3_gps)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F3E0; 4. ${esc(nl.s4_title)} / ${esc(tl2.s4_title)}</div>
  <ul>
    <li>${b(nl.s4_return, tl2.s4_return)}</li>
  </ul>
  <div class="callout">
    <strong>&#x1F4B0; Bonus</strong>
    ${b(nl.s4_bonus, tl2.s4_bonus)}
  </div>
</div>

<div class="section">
  <div class="section-title">&#x26A0;&#xFE0F; 5. ${esc(nl.s5_title)} / ${esc(tl2.s5_title)}</div>
  <div class="callout callout-danger">
    <strong>${esc(nl.s5_deductible)}</strong>
    <span class="tl">${esc(tl2.s5_deductible)}</span>
  </div>
  <ul>
    <li>${b(nl.s5_negligence, tl2.s5_negligence)}</li>
    <li>${b(nl.s5_no_third_party, tl2.s5_no_third_party)}</li>
  </ul>
</div>

<div class="contact-box">
  <p><strong>&#x1F4DE; ${esc(nl.s5_report)} / ${esc(tl2.s5_report)}</strong></p>
  <p>${esc(nl.s5_phone)} / ${esc(tl2.s5_phone)}:</p>
  <p class="phone">+31 184 675489</p>
</div>

<div class="section">
  <div class="section-title">&#x1F6E2;&#xFE0F; 6. ${esc(nl.s6_title)} / ${esc(tl2.s6_title)}</div>
  <ul>
    <li>${b(nl.s6_oil, tl2.s6_oil)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x26FD; 7. ${esc(nl.s7_title)} / ${esc(tl2.s7_title)}</div>
  <ul>
    <li>${b(nl.s7_no_highway, tl2.s7_no_highway)}</li>
    <li>${b(nl.s7_euro95, tl2.s7_euro95)}</li>
    <li>${b(nl.s7_wrong_fuel, tl2.s7_wrong_fuel)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F4B3; 8. ${esc(nl.s8_title)} / ${esc(tl2.s8_title)}</div>
  <ul>
    <li>${b(nl.s8_personal, tl2.s8_personal)}</li>
    <li>${b(nl.s8_replacement, tl2.s8_replacement)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F6AB; 9. ${esc(nl.s9_title)} / ${esc(tl2.s9_title)}</div>
  <p class="nl">${esc(nl.s9_no_mods)}</p>
  <p class="tl"><em>${esc(tl2.s9_no_mods)}</em></p>
  <ul>
    ${nl.s9_allowed.map((item, i) => `<li><span class="nl">${esc(item)}</span> <span class="tl">/ ${esc(tl2.s9_allowed[i])}</span></li>`).join('\n    ')}
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F6A8; 10. ${esc(nl.s10_title)} / ${esc(tl2.s10_title)}</div>
  <ul>
    <li>${b(nl.s10_recovery, tl2.s10_recovery)}</li>
    <li>${b(nl.s10_personal_items, tl2.s10_personal_items)}</li>
  </ul>
</div>

<h2>&#x1F4B8; ${esc(nl.pen_title)} / ${esc(tl2.pen_title)}</h2>
<table>
<thead>
  <tr><th>${esc(nl.pen_what)} / ${esc(tl2.pen_what)}</th><th style="text-align:right">${esc(nl.pen_cost)} / ${esc(tl2.pen_cost)}</th></tr>
</thead>
<tbody>
  <tr><td>&#x1F697; ${esc(nl.pen_not_return)}<br/><em>${esc(tl2.pen_not_return)}</em></td><td class="penalty-cost">&euro; 200</td></tr>
  <tr><td>&#x1F9F9; ${esc(nl.pen_not_clean)}<br/><em>${esc(tl2.pen_not_clean)}</em></td><td class="penalty-cost">&euro; 50</td></tr>
  <tr><td>&#x26FD; ${esc(nl.pen_not_refueled)}<br/><em>${esc(tl2.pen_not_refueled)}</em></td><td class="penalty-cost">&euro; 20</td></tr>
  <tr><td>&#x1F4B3; ${esc(nl.pen_fuel_card)}<br/><em>${esc(tl2.pen_fuel_card)}</em></td><td class="penalty-cost">&euro; 75</td></tr>
  <tr><td>&#x1F511; ${esc(nl.pen_key_lost)}<br/><em>${esc(tl2.pen_key_lost)}</em></td><td class="penalty-cost">&euro; 150</td></tr>
  <tr><td>&#x1F4B3; ${esc(nl.pen_chip_lost)}<br/><em>${esc(tl2.pen_chip_lost)}</em></td><td class="penalty-cost">&euro; 25</td></tr>
  <tr><td>&#x1F6AC; ${esc(nl.pen_smoking)}<br/><em>${esc(tl2.pen_smoking)}</em></td><td class="penalty-cost">&euro; 150</td></tr>
  <tr><td>&#x26A0;&#xFE0F; ${esc(nl.pen_own_risk)}<br/><em>${esc(tl2.pen_own_risk)}</em></td><td class="penalty-cost">&euro; 250</td></tr>
  <tr><td>&#x274C; ${esc(nl.pen_unreported)}<br/><em>${esc(tl2.pen_unreported)}</em></td><td class="penalty-cost">&euro; 500 +</td></tr>
  <tr><td>&#x1F6A8; ${esc(nl.pen_reckless)}<br/><em>${esc(tl2.pen_reckless)}</em></td><td class="penalty-cost">${esc(nl.pen_full_costs)} / ${esc(tl2.pen_full_costs)}</td></tr>
  <tr><td>&#x1F3CE;&#xFE0F; ${esc(nl.pen_speeding)}<br/><em>${esc(tl2.pen_speeding)}</em></td><td class="penalty-cost">&euro; 25 / week</td></tr>
</tbody>
</table>

<div class="signature-section">
  <div class="summary-box">
    <p><strong>&#x270D;&#xFE0F; ${esc(sNl.signAgreement)}</strong></p>
    <p><em>${esc(sTl2.signAgreement)}</em></p>
  </div>
  <p><strong>${esc(sNl.employee)} / ${esc(sTl2.employee)}:</strong> ${w}</p>
  <p><strong>${esc(sNl.date)} / ${esc(sTl2.date)}:</strong> ${esc(date)}</p>
  <br/>
  <p>${esc(sNl.signature)} / ${esc(sTl2.signature)}:</p>
  <div class="sig-line"></div>
</div>
</body></html>`;
    },
  },
  {
    id: 'tools_giveout_agreement',
    title: 'Gereedschap Uitgifteovereenkomst / Tools Agreement',
    description: 'General terms for tool handout — ownership, care, return requirements, damage/loss charges, and collection costs.',
    documentType: 'contract',
    icon: 'construction',
    generateHtml: (workerName: string, date: string, language: TemplateLang = 'en', companyName?: string, templateData?: TemplateData) => {
      const company = companyName || 'Optima Uitzendburo B.V.';
      const w = esc(workerName);
      const c = esc(company);
      const nl = tools.nl;
      const tl2 = language === 'nl' ? tools.en : tools[language];
      const sNl = shared.nl;
      const sTl2 = language === 'nl' ? shared.en : shared[language];

      const b = (nlText: string, tlText: string) => bil(nlText, tlText, false);
      const fieldsHtml = templateData?.fields ? renderFields(templateData.fields) : '';
      const photosHtml = templateData?.photoUrls?.length ? renderPhotos(templateData.photoUrls) : '';

      return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>${sharedStyles}</style></head><body>
<div class="header">
  <div class="company">${c}</div>
  <div class="meta">${esc(date)}</div>
</div>

<h1>&#x1F6E0;&#xFE0F; ${esc(nl.title)} / ${esc(tl2.title)}</h1>
<p><strong>${w}</strong></p>

${fieldsHtml}
${photosHtml}

<div class="summary-box">
  <p><strong>&#x1F4CB; ${esc(sNl.summaryTitle)} / ${esc(sTl2.summaryTitle)}</strong></p>
  <p>${t(nl.summary, company)}</p>
  <p><em>${t(tl2.summary, company)}</em></p>
</div>

<div class="section">
  <div class="section-title">&#x1F3E2; 1. ${esc(nl.s1_title)} / ${esc(tl2.s1_title)}</div>
  <ul>
    <li>${b(t(nl.s1_property, company), t(tl2.s1_property, company))}</li>
    <li>${b(nl.s1_never_yours, tl2.s1_never_yours)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F9F0; 2. ${esc(nl.s2_title)} / ${esc(tl2.s2_title)}</div>
  <ul>
    <li>${b(nl.s2_responsible, tl2.s2_responsible)}</li>
    <li>${b(nl.s2_intended, tl2.s2_intended)}</li>
    <li>${b(nl.s2_misuse, tl2.s2_misuse)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F4E6; 3. ${esc(nl.s3_title)} / ${esc(tl2.s3_title)}</div>
  <ul>
    <li>${b(t(nl.s3_return, company), t(tl2.s3_return, company))}</li>
    <li>${b(nl.s3_condition, tl2.s3_condition)}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x26A0;&#xFE0F; 4. ${esc(nl.s4_title)} / ${esc(tl2.s4_title)}</div>
  <div class="callout callout-danger">
    <strong>&#x1F4B0; ${esc(nl.s4_costs)}</strong><br/>
    <span class="tl">${esc(tl2.s4_costs)}</span>
  </div>
  <ul>
    <li>${b(t(nl.s4_report, company), t(tl2.s4_report, company))}</li>
  </ul>
</div>

<div class="section">
  <div class="section-title">&#x1F69A; 5. ${esc(nl.s5_title)} / ${esc(tl2.s5_title)}</div>
  <div class="callout">
    <strong>&#x1F4B8; ${t(nl.s5_collection, company)}</strong><br/>
    <span class="tl">${t(tl2.s5_collection, company)}</span>
  </div>
</div>

<div class="section">
  <div class="section-title">&#x1F4B3; 6. ${esc(nl.s6_title)} / ${esc(tl2.s6_title)}</div>
  <ul>
    <li>${b(nl.s6_deduction, tl2.s6_deduction)}</li>
    <li>${b(t(nl.s6_authorize, company), t(tl2.s6_authorize, company))}</li>
  </ul>
</div>

<div class="signature-section">
  <div class="summary-box">
    <p><strong>&#x270D;&#xFE0F; ${esc(nl.signConfirm)}</strong></p>
    <p><em>${esc(tl2.signConfirm)}</em></p>
  </div>
  <p><strong>${esc(sNl.employee)} / ${esc(sTl2.employee)}:</strong> ${w}</p>
  <p><strong>${esc(sNl.date)} / ${esc(sTl2.date)}:</strong> ${esc(date)}</p>
  <br/>
  <p>${esc(sNl.signature)} / ${esc(sTl2.signature)}:</p>
  <div class="sig-line"></div>
</div>
</body></html>`;
    },
  },
];
